import { AccountInfo, IPublicClientApplication } from "@azure/msal-browser";
import axios from "axios";
import enviroments from "../enviroments";
import { config, msalInstance } from "../utils/auth_azure";

/**
 * Obtenção do JWT do Conexão pelo Azure AD.
 *
 * Substitui `GET /api/autonomo/obter-por-email/{email}` como fonte de credencial. Aquele endpoint
 * é anônimo e devolve um JWT válido para qualquer e-mail de autônomo conhecido — não exige segredo
 * algum, basta saber o e-mail de alguém. Aqui o e-mail vem da claim de um token assinado pela
 * Microsoft e validado contra o JWKS no servidor, então ninguém obtém credencial de terceiro.
 *
 * Este é o token do **Conexão**. Não confundir com o token da identity do Sellit
 * (`TOKEN_KEY` no localStorage), que continua vindo de
 * `identity/app-web/conta-usuario/v1/gerar-token` e serve às APIs do próprio Sellit.
 *
 * A credencial trocada é o **ID token** do próprio Sellit, e não um access token da API do Conexão:
 * a App Registration do Conexão exige atribuição de usuário, e os autônomos não estão atribuídos.
 */

// Instância limpa de propósito: sem os interceptors de `axiosServices`, que anexam o token da
// identity do Sellit e reagem a 401 — um 401 vindo daqui realimentaria o fluxo em laço.
const conexaoAuthApi = axios.create({
  baseURL: enviroments.CONEXAO_URL || "",
  headers: { "Content-Type": "application/json" },
});

let tokenEmMemoria: string | null = null;
let obtencaoEmCurso: Promise<string | null> | null = null;

/** Descarta o token em memória. Chamar no logout e ao receber 401 do Conexão. */
export function limparTokenConexao(): void {
  tokenEmMemoria = null;
  obtencaoEmCurso = null;
}

interface OpcoesToken {
  /** Instância que acabou de autenticar. Sem ela, usa a instância global. */
  pca?: IPublicClientApplication;
  /** Conta devolvida pelo login. Sem ela, usa a conta ativa ou a primeira do cache. */
  account?: AccountInfo;
  /** ID token recém-vindo do `loginPopup`. Evita uma renovação silenciosa desnecessária. */
  idToken?: string;
}

/**
 * ID token do Azure AD para a conta que está logada no Sellit.
 *
 * A renovação usa `config.scopes` — os mesmos do login, que são do Microsoft Graph. O access token
 * que vem junto é descartado: o que interessa é o `idToken` da resposta, que o MSAL renova na mesma
 * chamada. Pedir os scopes do login e não um conjunto novo é deliberado: é um recurso que a conta
 * já usa, então não há consentimento, atribuição nem popup envolvidos.
 */
async function obterIdTokenAad(opcoes: OpcoesToken): Promise<string | null> {
  if (opcoes.idToken) return opcoes.idToken;

  const pca = opcoes.pca ?? msalInstance;
  const conta =
    opcoes.account ?? pca.getActiveAccount() ?? pca.getAllAccounts()[0];

  if (!conta) {
    console.warn(
      "[conexaoAuth] Sem conta do Azure AD; token do Conexão não será obtido."
    );
    return null;
  }

  const resposta = await pca.acquireTokenSilent({
    scopes: config.scopes,
    account: conta,
  });

  return resposta.idToken || null;
}

/**
 * Devolve o JWT do Conexão, reaproveitando o que já está em memória.
 *
 * As chamadas concorrentes compartilham a mesma promessa: o primeiro carregamento de tela dispara
 * várias requisições ao Conexão de uma vez, e sem isso cada uma abriria sua própria troca de token.
 */
export async function obterTokenConexao(
  opcoes: OpcoesToken = {}
): Promise<string | null> {
  if (tokenEmMemoria) return tokenEmMemoria;
  if (obtencaoEmCurso) return obtencaoEmCurso;

  obtencaoEmCurso = (async () => {
    try {
      const idTokenAad = await obterIdTokenAad(opcoes);
      if (!idTokenAad) return null;

      const resposta = await conexaoAuthApi.post("api/auth/token", null, {
        headers: { Authorization: `Bearer ${idTokenAad}` },
      });

      tokenEmMemoria = resposta?.data?.token ?? null;
      return tokenEmMemoria;
    } finally {
      obtencaoEmCurso = null;
    }
  })();

  return obtencaoEmCurso;
}

/**
 * Troca feita no login, com o ID token que o `loginPopup` acabou de devolver.
 *
 * Não relança: falhar aqui não pode impedir a entrada no Sellit, porque nem todo usuário é autônomo
 * no Conexão. Mas **registra em console.error**, para que a falha não fique invisível.
 */
export async function obterTokenConexaoNoLogin(
  pca: IPublicClientApplication,
  account: AccountInfo,
  idToken?: string
): Promise<string | null> {
  try {
    const token = await obterTokenConexao({ pca, account, idToken });

    if (!token) {
      console.error(
        "[conexaoAuth] Login concluído, mas o token do Conexão não foi obtido. " +
          "As telas que dependem da API do Conexão ficarão sem credencial."
      );
    }

    return token;
  } catch (erro) {
    console.error(
      "[conexaoAuth] Falha ao obter o token do Conexão no login:",
      erro
    );
    return null;
  }
}

/**
 * ID token do Azure AD da conta logada, sem interação.
 *
 * Exposto porque `GET /api/autonomo/v2/obter-por-email` é autenticado **pelo token do Azure AD**, e
 * não pelo JWT do Conexão: ele precisa verificar de quem é o e-mail consultado, e só o token da
 * Microsoft prova isso. Quem chama deve colocá-lo no `Authorization` explicitamente — assim o
 * interceptor do `instanceConexao`, que só age quando o header está ausente, não o sobrescreve.
 */
export async function obterIdTokenAadConexao(): Promise<string | null> {
  try {
    return await obterIdTokenAad({});
  } catch (erro) {
    console.error("[conexaoAuth] Falha ao obter o ID token do Azure AD:", erro);
    return null;
  }
}
